import React, {FC, useEffect, useRef, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {RouteComponentProps, useHistory} from 'react-router-dom'

import {
  createElement,
  ElementTypes,
  init,
  Payouts,
} from '@airwallex/components-sdk'
import {Alert, Box, CircularProgress, Grid} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {enqueueSnackbar} from 'notistack'

import AlertSnackBar from '@progyny/components/AlertSnackBar/AlertSnackBar'
import {AIRWALLEX_SCOPES} from '@progyny/const/airwallexScopes'
import {useAirwallexAuthorizationQuery} from '@progyny/generated/graphql'
import {useBeneficiarySave} from '@progyny/hooks/useBeneficiarySave'
import {ReimbursementMobileStepper} from '@progyny/pages/Budget/Reimbursement/ReimbursementMobileStepper'
import {useClaimStepMeta} from '@progyny/pages/Budget/hook/useClaimStepMeta'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useClaimAirwallexBeneficiary} from '@progyny/store/claims'
import {useClaim} from '@progyny/store/claims'
import {useLanguageCriteria} from '@progyny/store/languageStore'
import {useMeStore} from '@progyny/store/meStore'
import {REACT_APP_AIRWALLEX_ENV} from '@progyny/utils/env'

const BENEFICIARY_FORM_CONTAINER_ID = 'beneficiary-form-container'

type AirwallexLocale = NonNullable<Parameters<typeof init>[0]['locale']>

const AIRWALLEX_SUPPORTED_LOCALES: readonly AirwallexLocale[] = [
  'en',
  'zh',
  'ja',
  'ko',
  'ar',
  'fr',
  'es',
  'nl',
  'nl-NL',
  'de',
  'it',
  'zh-HK',
  'pl',
  'fi',
  'ru',
  'da',
  'id',
  'ms',
  'sv',
  'pt',
  'ro',
]

const toAirwallexLocale = (language: string): AirwallexLocale =>
  AIRWALLEX_SUPPORTED_LOCALES.includes(language as AirwallexLocale)
    ? (language as AirwallexLocale)
    : 'en'

const FORM_THEME: Payouts.BeneficiaryFormOptions['theme'] = {
  typography: {
    fontFamily: 'DM Sans',
    fontSize: 16,
    fontSizeLg: 18,
    fontWeightNormal: 400,
    inputPaddingVertical: '16.5px',
    inputPaddingHorizontal: '14px',
  },
}

// Builds the form customizations. The account country and currency are
// pre-filled via defaultValues and locked here (disabled:true). The country
// label is overridden with our own translated string.
const buildFormCustomizations = ({
  bankCountryCode,
  countryLabel,
}: {
  bankCountryCode?: string
  countryLabel: string
}): Payouts.BeneficiaryFormCustomizations => ({
  minHeight: 350,
  layout: [{name: 'entityInfo', hidden: true}],
  fields: {
    // Whitelist the transfer methods so the SDK auto-selects the first allowed
    // one that is available for the country. LOCAL is listed first so it is
    // preferred over SWIFT whenever a country supports LOCAL (per Airwallex
    // guidance: "use LOCAL over SWIFT for local currency when supported"). The
    // SDK auto-resolves the local clearing system (e.g. FAST for Singapore).
    //
    // The selector is NOT hidden: hiding it leaves paymentMethod: null in the
    // SDK's internal BeneficiaryFormSchema / TransferFormValidation calls, which
    // fail with "paymentMethods is required" and never render the bank fields.
    // Keeping it visible lets the SDK set the paymentMethod and render the
    // country-specific fields. When only one method is whitelisted/available
    // the selector shows a single locked option.
    transfer_methods: {whitelist: ['LOCAL', 'SWIFT']},
    'beneficiary.entity_type': {hidden: true},
    'beneficiary.address.country_code': {hidden: true},
    // Account holder name is pre-filled from the member profile and locked.
    'beneficiary.bank_details.account_name': {disabled: true, hintText: ' '},
    // Account currency is pre-filled from the budget currency and locked.
    'beneficiary.bank_details.account_currency': {disabled: true},
    ...(bankCountryCode && {
      // Pre-filled (saved or country of employment) and locked.
      'beneficiary.bank_details.bank_country_code': {
        disabled: true,
        label: countryLabel,
      },
    }),
  },
})

export type BeneficiaryAccountProps = Partial<RouteComponentProps> & {
  activeStep?: number
  setActiveStep?: React.Dispatch<React.SetStateAction<number>>
}

const BeneficiaryAccount: FC<BeneficiaryAccountProps> = ({
  activeStep,
  setActiveStep,
  match,
}) => {
  const {t} = useTranslation()
  const history = useHistory()
  const {getPath} = useRoutes()
  const {defaultLanguage} = useLanguageCriteria()
  const {saveBeneficiary, existingBeneficiary, beneficiaryLoading} =
    useBeneficiarySave()
  const {setAirwallexBeneficiaryPayload} = useClaimAirwallexBeneficiary()
  const user = useMeStore((s) => s.user)
  const countryOfEmployment = useMeStore((s) => s.countryCode)
  // Captured in a ref so the init effect can read the latest name without
  // adding `user` as a dependency (which would re-run init with an already-
  // consumed auth code).
  const accountNameRef = useRef<string>('')
  accountNameRef.current = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  // Captured in a ref for the same reason as accountNameRef: the init effect
  // must read the latest country of employment without re-running init (which
  // would reuse an already-consumed auth code).
  const countryOfEmploymentRef = useRef<string | undefined>(countryOfEmployment)
  countryOfEmploymentRef.current = countryOfEmployment

  // Account currency pre-filled from the member's current budget currency.
  // Captured in a ref for the same reason as the fields above.
  const accountCurrencyRef = useRef<string | undefined>(
    user?.member?.currentBudget?.currency ?? undefined
  )
  accountCurrencyRef.current =
    user?.member?.currentBudget?.currency ?? undefined

  // Captured in a ref so the init effect can read the latest label without
  // re-running (which would reuse an already-consumed auth code).
  const countryLabelRef = useRef<string>('')
  countryLabelRef.current = t(
    'budget_reimbursement_payments_beneficiary_account_country'
  )

  const isClaimFlow = activeStep !== undefined && !!match && !!setActiveStep

  const elementRef = useRef<ElementTypes['beneficiaryForm'] | undefined>(
    undefined
  )
  const localeRef = useRef<AirwallexLocale>(toAirwallexLocale(defaultLanguage))
  localeRef.current = toAirwallexLocale(defaultLanguage)

  // Ref so the init effect can read the latest value without depending on it —
  // existingBeneficiary in the deps would re-run init() with an already-used
  // auth code, causing invalid_grant on every subsequent query update.
  const existingBeneficiaryRef = useRef(existingBeneficiary)
  existingBeneficiaryRef.current = existingBeneficiary

  const [isReady, setIsReady] = useState(false)
  const [isInitError, setIsInitError] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {data: authData, error: authError} = useAirwallexAuthorizationQuery({
    variables: {scopes: [AIRWALLEX_SCOPES.TRANSFER_WRITE]},
    fetchPolicy: 'no-cache',
  })

  const {claimRequested} = useClaim()
  const {stepEnumList, isMedicallyNecessaryRelevant} =
    useClaimStepMeta(claimRequested)

  const previousStepPath = isMedicallyNecessaryRelevant
    ? `${getPath(RouteEnum.BUDGET)}/reimbursement/medical-information`
    : `${getPath(RouteEnum.BUDGET)}/reimbursement/documents`

  useEffect(() => {
    if (isClaimFlow && !claimRequested) {
      history.push(`${getPath(RouteEnum.BUDGET)}/reimbursement/claim`)
    }
  }, [claimRequested, isClaimFlow, getPath, history])

  useEffect(() => {
    if (!isClaimFlow || !setActiveStep) return
    const index = stepEnumList.findIndex((step) => step === 'PAYMENT_DETAILS')
    if (index !== -1) setActiveStep(index)
  }, [setActiveStep, stepEnumList, isClaimFlow])

  useEffect(() => {
    if (authError) setIsInitError(true)
  }, [authError])

  useEffect(() => {
    if (!authData || beneficiaryLoading) return

    let mounted = true

    const mountBeneficiaryElement = async (
      defaultValues: Payouts.BeneficiaryFormDefaultValues
    ): Promise<void> => {
      const element = await createElement('beneficiaryForm', {
        defaultValues,
        customizations: buildFormCustomizations({
          // Lock the country that is actually shown (saved value for an existing
          // beneficiary, otherwise the pre-filled country of employment).
          bankCountryCode:
            defaultValues.beneficiary?.bank_details?.bank_country_code,
          countryLabel: countryLabelRef.current,
        }),
        theme: FORM_THEME,
      })

      if (!mounted) {
        element.destroy()
        return
      }

      element.mount(`#${BENEFICIARY_FORM_CONTAINER_ID}`)

      element.on('ready', () => {
        if (mounted) setIsReady(true)
      })

      element.on('error', () => {
        if (mounted) setIsInitError(true)
      })

      elementRef.current = element
    }

    const initAndMount = async () => {
      try {
        const {authorizationCode, clientId, codeVerifier} =
          authData.airwallexAuthorization

        await init({
          locale: localeRef.current,
          env: REACT_APP_AIRWALLEX_ENV as 'demo' | 'prod',
          authCode: authorizationCode,
          clientId,
          codeVerifier,
        })

        const baseDefaults = existingBeneficiaryRef.current ?? {
          beneficiary: {entity_type: 'PERSONAL'},
        }

        const savedBankDetails = baseDefaults.beneficiary?.bank_details
        const accountName = accountNameRef.current || undefined
        // For an existing beneficiary keep the saved country/currency; only fall
        // back to the member's country of employment / budget currency when
        // creating a new one.
        const bankCountryCode =
          savedBankDetails?.bank_country_code ||
          countryOfEmploymentRef.current ||
          undefined
        const accountCurrency =
          savedBankDetails?.account_currency ||
          accountCurrencyRef.current ||
          undefined

        // bank_country_code and account_currency are top-level fields rendered
        // on initial load, so defaultValues are applied to them. Both are locked
        // via disabled:true in buildFormCustomizations. account_name is likewise
        // a plain field that renders on load.
        const bankDetails =
          accountName || bankCountryCode || accountCurrency
            ? {
                ...savedBankDetails,
                ...(accountName && {account_name: accountName}),
                ...(bankCountryCode && {bank_country_code: bankCountryCode}),
                ...(accountCurrency && {account_currency: accountCurrency}),
              }
            : undefined

        await mountBeneficiaryElement({
          ...baseDefaults,
          beneficiary: {
            ...baseDefaults.beneficiary,
            ...(bankDetails && {bank_details: bankDetails}),
          },
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        if (mounted) setIsInitError(true)
      }
    }

    initAndMount()

    return () => {
      mounted = false
      elementRef.current?.unmount()
      elementRef.current?.destroy()
      elementRef.current = undefined
    }
  }, [authData, beneficiaryLoading])

  const handleSubmit = async () => {
    if (!elementRef.current) return

    setIsSubmitting(true)
    setIsSubmitError(false)

    try {
      const submitResult = await elementRef.current.submit()
      if (submitResult.errors) return

      const bankCountry =
        submitResult.values.beneficiary?.bank_details?.bank_country_code

      // The SDK adds an internal `type` field at runtime (e.g. "BANK_ACCOUNT")
      // that is not part of the Airwallex beneficiary API schema. Forwarding it
      // causes BeneficiaryFormSchema validation to fail with BAD_USER_INPUT.
      const {type: _type, ...beneficiary} = (submitResult.values.beneficiary ??
        {}) as Payouts.BeneficiaryFormValue['beneficiary'] & {type?: string}

      // The SDK auto-populates swift_code from the bank lookup even for LOCAL
      // transfers. Sending swift_code alongside transfer_methods: ["LOCAL"]
      // causes Airwallex to return transfer_method_unsupported.
      const isSwiftTransfer =
        submitResult.values.transfer_methods?.includes('SWIFT')
      const {
        swift_code: _swift,
        intermediary_bank_swift_code: _intermediary,
        ...bankDetailsWithoutSwift
      } = beneficiary.bank_details ?? {}

      const payload: Payouts.BeneficiaryFormValue = {
        ...submitResult.values,
        beneficiary: {
          ...beneficiary,
          bank_details: {
            ...bankDetailsWithoutSwift,
            ...(isSwiftTransfer && {
              swift_code: beneficiary.bank_details?.swift_code,
              intermediary_bank_swift_code:
                beneficiary.bank_details?.intermediary_bank_swift_code,
            }),
          },
          address: {
            ...beneficiary.address,
            ...(bankCountry && {country_code: bankCountry}),
          },
        },
      }

      const result = await saveBeneficiary(payload)

      if (result?.success) {
        setAirwallexBeneficiaryPayload({
          accountName: payload.beneficiary?.bank_details?.account_name,
          accountNumber: payload.beneficiary?.bank_details?.account_number,
          iban: payload.beneficiary?.bank_details?.iban,
          bankCountryCode: payload.beneficiary?.bank_details?.bank_country_code,
          accountCurrency: payload.beneficiary?.bank_details?.account_currency,
          swiftCode: payload.beneficiary?.bank_details?.swift_code,
          accountRoutingType1:
            payload.beneficiary?.bank_details?.account_routing_type1,
          accountRoutingValue1:
            payload.beneficiary?.bank_details?.account_routing_value1,
          accountRoutingType2:
            payload.beneficiary?.bank_details?.account_routing_type2,
          accountRoutingValue2:
            payload.beneficiary?.bank_details?.account_routing_value2,
        })
        if (isClaimFlow && match) {
          const parent = match.url.split('/').slice(0, -1).join('/')
          history.push(`${parent}/overview`)
        } else {
          enqueueSnackbar({
            message: t(
              'budget_reimbursement_payments_beneficiary_save_success'
            ),
            variant: 'success',
          })
        }
      } else {
        enqueueSnackbar({
          message:
            result?.errors?.[0]?.messages?.[0] ??
            t('budget_reimbursement_payments_beneficiary_save_error'),
          variant: 'error',
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      setIsSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Grid
      width={{xs: '100%', md: '85%'}}
      mt={2}
      container
      justifyContent='center'
      data-testid='payments-beneficiary-form'
    >
      <Grid item xs={12} md={7}>
        <AlertSnackBar
          open={isInitError}
          handleClose={() => setIsInitError(false)}
          type='error'
          message={t('budget_reimbursement_payments_beneficiary_init_error')}
        />
        <AlertSnackBar
          open={isSubmitError}
          handleClose={() => setIsSubmitError(false)}
          type='error'
          message={t('budget_reimbursement_payments_beneficiary_submit_error')}
        />
        <Box position='relative' minHeight={350}>
          {!isReady && (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              minHeight={350}
              data-testid='payments-beneficiary-loader'
            >
              <CircularProgress />
            </Box>
          )}

          <div
            id={BENEFICIARY_FORM_CONTAINER_ID}
            data-testid='payments-beneficiary-container'
            style={{minHeight: '350px', marginBottom: '20px'}}
          />
        </Box>

        {!isClaimFlow && (
          <Alert severity='info' sx={{backgroundColor: '#F4F6FF', my: 3}}>
            <Trans i18nKey='profile_bank_details_additional_info_note' />
          </Alert>
        )}

        {!isClaimFlow && (
          <Box mt={2} display='flex' justifyContent='start'>
            <PGButton
              variant='contained'
              label={t('budget_reimbursement_payments_beneficiary_submit')}
              onClick={handleSubmit}
              disabled={!isReady || isSubmitting}
              loading={isSubmitting}
              data-testid='payments-beneficiary-submit'
            />
          </Box>
        )}

        {isClaimFlow && (
          <Box display='flex' gap={2} mt={2} justifyContent={'center'}>
            <PGButton
              variant='outlined'
              label={t('budget_reimbursement_documents_go_back')}
              onClick={() => history.push(previousStepPath)}
            />
            <PGButton
              variant='contained'
              label={t('common_next')}
              onClick={handleSubmit}
              disabled={!isReady || isSubmitting}
              loading={isSubmitting}
              data-testid='payments-beneficiary-submit'
            />
          </Box>
        )}
      </Grid>

      {isClaimFlow && (
        <ReimbursementMobileStepper
          isDisabled={!isReady || isSubmitting}
          activeStep={activeStep!}
          toCancelPath={previousStepPath}
          toContinuePath={handleSubmit}
          cancelButtonText={t('budget_reimbursement_documents_go_back')}
        />
      )}
    </Grid>
  )
}

export default BeneficiaryAccount
